import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Delete,
  Search,
  Receipt,
  Print,
  Save,
  ExpandMore,
  Payment,
  AttachMoney,
  CreditCard,
  PhoneAndroid,
  AccountBalance,
  AccountBalanceWallet,
  Schedule,
  CardGiftcard,
  MoreHoriz,
  Close,
  Cancel,
  Edit,
  Inventory,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { getInvoice, updateInvoice } from '../redux/actions/invoiceActions';
import { getProducts } from '../redux/actions/productActions';
import { getCustomers } from '../redux/actions/customerActions';
import { getActivePaymentModes } from '../redux/actions/paymentModeActions';
import { getClosedChitsForCustomer } from '../redux/actions/chitActions';
import { keyframes } from '@mui/material/styles';

// Animations for success dialog
const tickAnimation = keyframes`
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const scaleIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

const fadeInUp = keyframes`
  0% { 
    opacity: 0;
    transform: translateY(20px);
  }
  100% { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const EditInvoice = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const { currentInvoice, loading, error } = useSelector((state) => state.invoices);
  const { products } = useSelector((state) => state.products);
  const { customers } = useSelector((state) => state.customers);
  const { activePaymentModes } = useSelector((state) => state.paymentModes);
  const { closedChits, closedChitsLoading } = useSelector((state) => state.chits);

  // Form state
  const [customer, setCustomer] = useState(null);
  const [openCustomerSearchDialog, setOpenCustomerSearchDialog] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [category, setCategory] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [paymentModes, setPaymentModes] = useState([]);
  const [oldMetals, setOldMetals] = useState([]);
  const [selectedChits, setSelectedChits] = useState([]);
  const [loadingInvoice, setLoadingInvoice] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Success Dialog state
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    title: 'Success',
    message: '',
    invoiceNo: '',
    invoiceDate: '',
    amount: '',
    invoiceId: null
  });

  // New item state
  const [searchProduct, setSearchProduct] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [weight, setWeight] = useState('');
  const [rate, setRate] = useState('');
  const [description, setDescription] = useState('');
  const [huid, setHuid] = useState('');
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [deleteItemDialog, setDeleteItemDialog] = useState({ open: false, index: null });

  // New old metal state
  const [newOldMetal, setNewOldMetal] = useState({
    description: '',
    weight: '',
    rate: '',
    amount: 0
  });

  // Category options
  const categoryOptions = [
    { value: 'Gold', label: 'Gold', hsnCode: '7113' },
    { value: 'Silver', label: 'Silver', hsnCode: '7114' },
    { value: 'Diamond', label: 'Diamond', hsnCode: '7101' },
    { value: 'GENERAL', label: 'General', hsnCode: '7113' },
  ];

  const normalizeHsn = (value) => {
    const raw = String(value ?? '').trim();
    const upper = raw.toUpperCase();
    if (!raw) return '';
    if (raw === '-' || upper === 'N/A' || upper === 'NA') return '';
    return raw;
  };

  // Payment mode icons mapping
  const paymentIcons = {
    'AttachMoney': <AttachMoney />,
    'CreditCard': <CreditCard />,
    'PhoneAndroid': <PhoneAndroid />,
    'AccountBalance': <AccountBalance />,
    'Receipt': <Receipt />,
    'AccountBalanceWallet': <AccountBalanceWallet />,
    'Schedule': <Schedule />,
    'CardGiftcard': <CardGiftcard />,
    'MoreHoriz': <MoreHoriz />,
  };

  // Function to capitalize first letter of each word
  const toTitleCase = (str) => {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Success Dialog helper functions
  const showSuccessDialog = (message, title = 'Success', invoiceData = null) => {
    setSuccessDialog({
      open: true,
      title,
      message,
      invoiceNo: invoiceData?.invoice_no || currentInvoice?.invoice_no || '',
      invoiceDate: invoiceData?.invoice_date || currentInvoice?.invoice_date || invoiceDate || '',
      amount: invoiceData?.grand_total || currentInvoice?.grand_total || '',
      invoiceId: invoiceData?.id || currentInvoice?.id || null
    });
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialog({
      open: false,
      title: 'Success',
      message: '',
      invoiceNo: '',
      invoiceDate: '',
      amount: '',
      invoiceId: null
    });
    // Navigate to invoices list after closing dialog
    navigate('/invoices');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'success' });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (id) {
      loadInvoice();
    }
  }, [id]);

  useEffect(() => {
    dispatch(getProducts({ page: 1, limit: 1000 }));
    // Don't load all customers on mount - use search dialog instead
    dispatch(getActivePaymentModes());
  }, [dispatch]);

  // Search customers with debounce
  useEffect(() => {
    if (!openCustomerSearchDialog) return;

    const timeoutId = setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        await dispatch(getCustomers({ 
          page: 1, 
          limit: 50, 
          search: customerSearchTerm.trim() || undefined 
        }));
      } catch (error) {
        // Handle error
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300); // Debounce 300ms

    return () => clearTimeout(timeoutId);
  }, [customerSearchTerm, openCustomerSearchDialog, dispatch]);

  // Use customers from Redux store for search results
  useEffect(() => {
    if (openCustomerSearchDialog) {
      setCustomerSearchResults(customers || []);
    }
  }, [customers, openCustomerSearchDialog]);

  useEffect(() => {
    if (currentInvoice) {
      console.log('Loading invoice data:', currentInvoice);
      
      // Process and populate form data
      // First try to get customer from invoice data (if included), then from Redux store
      let selectedCustomer = null;
      if (currentInvoice.customer) {
        // Customer data is included in invoice response
        selectedCustomer = currentInvoice.customer;
      } else if (currentInvoice.customer_id && customers.length > 0) {
        // Find customer from Redux store
        selectedCustomer = customers.find(c => c.id === currentInvoice.customer_id);
      } else if (currentInvoice.customer_id) {
        // Customer not in store yet, fetch it
        dispatch(getCustomers({ page: 1, limit: 1000 })).then(() => {
          // Will be set in next render when customers are loaded
        });
      }
      setCustomer(selectedCustomer || null);
      setCategory(currentInvoice.category || 'GENERAL');
      setInvoiceDate(currentInvoice.invoice_date ? new Date(currentInvoice.invoice_date).toISOString().split('T')[0] : '');
      
      // Process items with all fields including final_amount and gst_percent
      const processedItems = (currentInvoice.items || []).map(item => ({
        // Jewellery HSNs are always 3% GST (PDFs are generated with 3% too)
        // This also fixes old invoices created when Tax Master had incorrect GST% (e.g., 18% for 7114).
        product_id: item.product_id,
        qty: item.qty || 1,
        weight: parseFloat(item.weight || 0),
        rate: parseFloat(item.rate || 0),
        description: item.description || item.product?.name || '',
        huid: item.huid || item.product?.huid || '',
        gst_percent: (() => {
          const categoryHsn = categoryOptions.find(
            (opt) => String(opt.value).toLowerCase() === String(currentInvoice.category || '').toLowerCase()
          )?.hsnCode;

          const hsn =
            normalizeHsn(item.product?.hsn_code) ||
            normalizeHsn(item.product?.hsnCode) ||
            normalizeHsn(item.hsn_code) ||
            normalizeHsn(item.hsnCode) ||
            normalizeHsn(categoryHsn);

          if (['7113', '7114', '7117', '7118'].includes(hsn)) return 3;
          const parsed = parseFloat(item.gst_percent);
          return Number.isFinite(parsed) && parsed > 0 ? parsed : 3;
        })(),
        final_amount: item.final_amount ? parseFloat(item.final_amount) : null,
        total_amount: parseFloat(item.total_amount || 0),
        amount: (() => {
          const existingAmount = parseFloat(item.amount);
          if (Number.isFinite(existingAmount) && existingAmount > 0) return existingAmount;

          const finalAmount = parseFloat(item.final_amount);
          const gstPercent = parseFloat(item.gst_percent);
          if (Number.isFinite(finalAmount) && finalAmount > 0 && Number.isFinite(gstPercent) && gstPercent >= 0) {
            // derive taxable value from final amount (which includes GST)
            return finalAmount / (1 + (gstPercent / 100));
          }

          // last resort
          const w = parseFloat(item.weight || 0);
          const r = parseFloat(item.rate || 0);
          return (Number.isFinite(w) ? w : 0) * (Number.isFinite(r) ? r : 0);
        })(),
        product: item.product || { hsn_code: '7113', name: item.description }
      }));
      setItems(processedItems);

      // Process payment modes with proper mode codes
      const processedPayments = (currentInvoice.payments || []).map(payment => ({
        modeCode: payment.payment_mode_code,
        modeId: payment.payment_mode_code,  // Use code as ID for matching
        modeName: payment.payment_mode?.name || payment.payment_mode_code,
        amount: parseFloat(payment.amount || 0),
        reference: payment.reference_number || '',
        bankDetails: payment.bank_details || ''
      }));
      
      console.log('Processed payments:', processedPayments);
      setPaymentModes(processedPayments);

      // Process old metals
      const processedOldMetals = (currentInvoice.oldMetalExchanges || []).map(metal => ({
        description: metal.description || '',
        weight: parseFloat(metal.weight || 0),
        rate: parseFloat(metal.rate_per_gram || metal.rate || 0),
        amount: parseFloat(metal.total_value || metal.amount || 0)
      }));
      setOldMetals(processedOldMetals);

      // Set discount
      setDiscount(parseFloat(currentInvoice.discount || 0));
      
      // Note: Chits and orders are not editable in edit mode, 
      // but we can display them if needed for reference
      setSelectedChits([]);
      setLoadingInvoice(false);
    }
  }, [currentInvoice, customers, dispatch]);

  // Update customer when customers are loaded and invoice has customer_id
  useEffect(() => {
    if (currentInvoice?.customer_id && customers.length > 0 && !customer) {
      const foundCustomer = customers.find(c => c.id === currentInvoice.customer_id);
      if (foundCustomer) {
        setCustomer(foundCustomer);
      }
    }
  }, [currentInvoice, customers, customer]);

  // Fetch closed chits when customer is selected
  useEffect(() => {
    if (customer?.id) {
      dispatch(getClosedChitsForCustomer(customer.id));
    } else {
      setSelectedChits([]);
    }
  }, [customer, dispatch]);

  const loadInvoice = async () => {
    try {
      await dispatch(getInvoice(id));
    } catch (error) {
      console.error('Error loading invoice:', error);
    }
  };

  // Filter products based on selected category
  const filteredProducts = products.filter(product => {
    if (!category) return true;
    return product.category === category;
  });

  const calculateItemTotal = (item) => {
    const baseAmount = (item.weight || 0) * (item.rate || 0);
    const gstAmount = baseAmount * 0.03; // 3% GST
    return Math.round(baseAmount + gstAmount);
  };

  const calculateOldMetalTotal = () => {
    return oldMetals.reduce((sum, metal) => sum + (metal.amount || 0), 0);
  };

  const calculateChitTotal = () => {
    return selectedChits.reduce((sum, chit) => sum + (chit.closure_amount || 0), 0);
  };

  const calculateTotals = () => {
    // If we have the original invoice data, use those totals instead of recalculating
    // This preserves the exact amounts from the database, especially for bargained invoices
    if (currentInvoice && currentInvoice.grand_total) {
      const cgst = parseFloat(currentInvoice.cgst) || 0;
      const sgst = parseFloat(currentInvoice.sgst) || 0;
      const igst = parseFloat(currentInvoice.igst) || 0;
      const subtotal = parseFloat(currentInvoice.subtotal) || 0;
      const discountAmt = parseFloat(currentInvoice.discount) || 0;
      const oldMetalAmt = parseFloat(currentInvoice.old_metal_total) || 0;
      const chitAmt = parseFloat(currentInvoice.chit_amount) || 0;
      const orderAmt = parseFloat(currentInvoice.order_amount) || 0;
      
      return {
        subtotal: Math.round(subtotal),
        discountAmount: Math.round(discountAmt),
        subtotalAfterDiscount: Math.round(subtotal - discountAmt),
        oldMetalTotal: Math.round(oldMetalAmt),
        chitTotal: Math.round(chitAmt),
        orderAmount: Math.round(orderAmt),
        subtotalAfterDeductions: Math.round(subtotal - oldMetalAmt - chitAmt - orderAmt - discountAmt),
        gstAmount: Math.round(cgst + sgst + igst),
        grandTotal: Math.round(parseFloat(currentInvoice.grand_total) || 0)
      };
    }

    // Fallback: Calculate from items (for new/modified invoices)
    const subtotal = items.reduce((sum, item) => {
      // Check if item has final_amount (bargaining), use that instead
      if (item.final_amount && item.final_amount > 0) {
        return sum + item.final_amount;
      }
      const baseAmount = (item.weight || 0) * (item.rate || 0);
      return sum + baseAmount;
    }, 0);

    const discountAmount = discount || 0;
    const subtotalAfterDiscount = subtotal - discountAmount;

    const oldMetalTotal = calculateOldMetalTotal();
    const chitTotal = calculateChitTotal();
    const subtotalAfterDeductions = subtotalAfterDiscount - oldMetalTotal - chitTotal;
    
    const gstAmount = subtotalAfterDeductions * 0.03; // 3% GST
    const grandTotal = subtotalAfterDeductions + gstAmount;

    return {
      subtotal: Math.round(subtotal),
      discountAmount: Math.round(discountAmount),
      subtotalAfterDiscount: Math.round(subtotalAfterDiscount),
      oldMetalTotal: Math.round(oldMetalTotal),
      chitTotal: Math.round(chitTotal),
      subtotalAfterDeductions: Math.round(subtotalAfterDeductions),
      gstAmount: Math.round(gstAmount),
      grandTotal: Math.round(grandTotal)
    };
  };

  const calculatePaymentTotal = () => {
    return paymentModes.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  };

  const getRemainingAmount = () => {
    const totals = calculateTotals();
    return totals.grandTotal - calculatePaymentTotal();
  };

  const getPaymentIcon = (iconName) => {
    return paymentIcons[iconName] || <MoreHoriz />;
  };

  const handleAddItem = () => {
    if (!selectedProduct || !quantity) return;

    const itemWeight = parseFloat(weight) || selectedProduct.weight || 0;
    const itemRate = parseFloat(rate) || selectedProduct.price_per_gram || 0;
    const baseAmount = itemWeight * itemRate; // Amount without tax

    const newItem = {
      product_id: selectedProduct.id,
      product: selectedProduct,
      qty: parseInt(quantity),
      weight: itemWeight,
      rate: itemRate,
      description: description || selectedProduct.name,
      huid: huid || selectedProduct.huid || '',
      gst_percent: 3,
      amount: baseAmount, // Amount without tax
      total_amount: calculateItemTotal({
        weight: itemWeight,
        rate: itemRate,
      }),
    };

    setItems([...items, newItem]);
    
    // Reset form and close dialogs
    setSelectedProduct(null);
    setQuantity(1);
    setWeight('');
    setRate('');
    setDescription('');
    setHuid('');
    setSearchProduct('');
    setOpenProductDialog(false);
  };

  const handleRemoveItem = (index) => {
    setDeleteItemDialog({ open: true, index });
  };

  const handleConfirmDeleteItem = () => {
    if (deleteItemDialog.index !== null) {
      setItems(items.filter((_, i) => i !== deleteItemDialog.index));
      setDeleteItemDialog({ open: false, index: null });
    }
  };

  const handleCancelDeleteItem = () => {
    setDeleteItemDialog({ open: false, index: null });
  };

  const handleFinalAmountChange = (index, value) => {
    setItems(items.map((item, i) => {
      if (i === index) {
        const finalAmount = value ? parseFloat(value) : null;
        let amount = null; // Amount without tax
        
        if (finalAmount && finalAmount > 0) {
          // Get GST percentage for this item
          let gstPercent = item.gst_percent || 3; // Default GST
          if (item.product && item.product.hsn_code) {
            // You can add tax lookup logic here if needed
            gstPercent = item.gst_percent || 3;
          }
          
          // Calculate amount without tax from final amount (which includes tax)
          // If final amount is 100 and GST is 3%, then amount = 100 / (1 + 0.03) = 97.09
          amount = finalAmount / (1 + (gstPercent / 100));
        }
        
        return { 
          ...item, 
          final_amount: finalAmount,
          amount: amount // Store amount without tax
        };
      }
      return item;
    }));
  };

  const handleAddOldMetal = () => {
    if (newOldMetal.description && newOldMetal.weight && newOldMetal.rate) {
      const weight = parseFloat(newOldMetal.weight);
      const rate = parseFloat(newOldMetal.rate);
      const amount = weight * rate;
      
      const oldMetal = {
        description: newOldMetal.description,
        weight: weight,
        rate: rate,
        amount: amount
      };

      setOldMetals([...oldMetals, oldMetal]);
      setNewOldMetal({ description: '', weight: '', rate: '', amount: 0 });
    }
  };

  const handleRemoveOldMetal = (index) => {
    setOldMetals(oldMetals.filter((_, i) => i !== index));
  };

  const handlePaymentModeToggle = (mode) => {
    const existingPayment = paymentModes.find(p => p.modeId === mode.code);
    
    if (existingPayment) {
      // Remove payment mode
      setPaymentModes(paymentModes.filter(p => p.modeId !== mode.code));
    } else {
      // Add payment mode
      const newPayment = {
        modeId: mode.code,
        modeName: mode.name,
        amount: 0,
        reference: '',
        bankDetails: ''
      };
      setPaymentModes([...paymentModes, newPayment]);
    }
  };

  const handlePaymentAmountChange = (modeId, amount) => {
    setPaymentModes(paymentModes.map(payment => 
      payment.modeId === modeId 
        ? { ...payment, amount: parseFloat(amount) || 0 }
        : payment
    ));
  };

  const handlePaymentReferenceChange = (modeId, reference) => {
    setPaymentModes(paymentModes.map(payment => 
      payment.modeId === modeId 
        ? { ...payment, reference }
        : payment
    ));
  };

  const handlePaymentBankDetailsChange = (modeId, bankDetails) => {
    setPaymentModes(paymentModes.map(payment => 
      payment.modeId === modeId 
        ? { ...payment, bankDetails }
        : payment
    ));
  };

  const handleChitToggle = (chit) => {
    const isSelected = selectedChits.some(c => c.id === chit.id);
    if (isSelected) {
      setSelectedChits(selectedChits.filter(c => c.id !== chit.id));
    } else {
      setSelectedChits([...selectedChits, chit]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get current totals to preserve them
    const totals = calculateTotals();
    
    const invoiceData = {
      customer_id: customer?.id || null,
      invoice_date: invoiceDate,
      category: category,
      items: items.map(item => ({
        product_id: item.product_id,
        qty: item.qty,
        weight: item.weight,
        rate: item.rate,
        description: item.description,
        huid: item.huid || '',
        hsnCode: (() => {
          const categoryHsn = categoryOptions.find(
            (opt) => String(opt.value).toLowerCase() === String(category || '').toLowerCase()
          )?.hsnCode;
          return (
            normalizeHsn(item.product?.hsn_code) ||
            normalizeHsn(item.product?.hsnCode) ||
            normalizeHsn(categoryHsn) ||
            '7113'
          );
        })(),
        gst_percent: item.gst_percent || 3,
        final_amount: item.final_amount || null  // ✅ Include final_amount if exists
      })),
      payment_modes: paymentModes.map(payment => ({
        mode_code: payment.modeId,
        amount: payment.amount,
        reference: payment.reference,
        bank_details: payment.bankDetails
      })),
      old_metals: oldMetals.map(metal => ({
        description: metal.description,
        weight: metal.weight,
        rate: metal.rate,
        amount: metal.amount
      })),
      selected_chits: selectedChits,
      discount: discount,
      // Send the actual totals from database to preserve them
      preserve_totals: true,
      grand_total: totals.grandTotal,
      subtotal: totals.subtotal,
      gst_amount: totals.gstAmount
    };

    console.log('Updating invoice with data:', invoiceData);

    try {
      const result = await dispatch(updateInvoice(id, invoiceData));
      if (result.success) {
        // Show success dialog with invoice details
        showSuccessDialog(
          'Invoice updated successfully!',
          'Invoice Updated',
          result.data || currentInvoice
        );
      } else {
        // Show error if update failed
        setSnackbar({ 
          open: true, 
          message: result.message || 'Failed to update invoice', 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'An error occurred while updating invoice', 
        severity: 'error' 
      });
    }
  };

  if (loadingInvoice) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const totals = calculateTotals();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 4 }}>
      {/* Modern Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
          mb: 3,
          borderRadius: '0 0 16px 16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
              <Edit sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Edit Invoice
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Modify invoice #{currentInvoice?.invoice_no}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Close />}
              onClick={() => navigate('/invoices')}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                  borderColor: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSubmit}
              disabled={loading || items.length === 0}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'white'
                }
              }}
            >
              Update Invoice
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: '1600px', mx: 'auto', px: { xs: 2, md: 3 } }}>
      <Grid container spacing={3}>
        {/* Customer Selection - Modern Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  <PhoneAndroid />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Customer Details
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="Customer"
                value={customer ? customer.name : ''}
                placeholder="Click to search customer..."
                size="small"
                onFocus={(e) => {
                  e.target.blur();
                  if (!openCustomerSearchDialog) {
                    setOpenCustomerSearchDialog(true);
                    setCustomerSearchTerm('');
                  }
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!openCustomerSearchDialog) {
                            setOpenCustomerSearchDialog(true);
                            setCustomerSearchTerm('');
                          }
                        }}
                        edge="end"
                      >
                        <Search color="action" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  mb: 2,
                  cursor: 'pointer',
                  '& .MuiInputBase-input': {
                    cursor: 'pointer'
                  }
                }}
              />
              {customer && (
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: 'primary.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'primary.100'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.dark">
                      {toTitleCase(customer.name)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Grid container spacing={1}>
                    {customer.address && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Address</Typography>
                        <Typography variant="body2" fontWeight="medium">{customer.address}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body2" fontWeight="medium">{customer.phone || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2" fontWeight="medium">{customer.email || 'N/A'}</Typography>
                    </Grid>
                    {customer.gstin && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">GSTIN</Typography>
                        <Typography variant="body2" fontWeight="medium">{customer.gstin}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Category & Date Selection - Modern Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                  <Inventory />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Invoice Settings
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Item Category</InputLabel>
                  <Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    label="Item Category"
                  >
                    <MenuItem value="">
                      <em>Select Category</em>
                    </MenuItem>
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: option.value === 'Gold' ? '#FFD700' :
                                      option.value === 'Silver' ? '#C0C0C0' : '#B9F2FF'
                            }}
                          />
                          {option.label} ({option.hsnCode})
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  label="Invoice Date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                {category && (
                  <Box sx={{ 
                    mt: 1, 
                    p: 2, 
                    bgcolor: 'success.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'success.100'
                  }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Category Info
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">Products:</Typography>
                      <Chip label={filteredProducts.length} size="small" color="success" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight="medium">HSN:</Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {categoryOptions.find(opt => opt.value === category)?.hsnCode}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Info - Modern Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                  <Receipt />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Invoice Info
                </Typography>
              </Box>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'info.50', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'info.100'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Invoice No:</Typography>
                  <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
                    {currentInvoice?.invoice_no || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Date:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {invoiceDate ? new Date(invoiceDate).toLocaleDateString('en-IN') : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Closed Chits Section */}
        {customer && closedChits && closedChits.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                    <CardGiftcard />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Available Closed Chits
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select closed chits to apply as credit against this invoice
                    </Typography>
                  </Box>
                </Box>
                
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Select</TableCell>
                        <TableCell>Chit Number</TableCell>
                        <TableCell>Group</TableCell>
                        <TableCell>Closure Type</TableCell>
                        <TableCell>Closure Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {closedChits.map((chit) => {
                        const isSelected = selectedChits.some(c => c.id === chit.id);
                        return (
                          <TableRow key={chit.id}>
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleChitToggle(chit)}
                                color="primary"
                              />
                            </TableCell>
                            <TableCell>{chit.chit_number}</TableCell>
                            <TableCell>{chit.group_name}</TableCell>
                            <TableCell>{chit.closure_type}</TableCell>
                            <TableCell>{new Date(chit.closure_date).toLocaleDateString()}</TableCell>
                            <TableCell>₹{parseFloat(chit.closure_amount).toFixed(2)}</TableCell>
                            <TableCell>
                              <Chip 
                                label={chit.status} 
                                color={chit.status === 'CLOSED' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Items Section - Modern Card */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                    <Inventory />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Invoice Items
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add products to the invoice
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      if (!category) {
                        alert('Please select a category before adding items to the invoice.');
                        return;
                      }
                      setOpenProductDialog(true);
                      setSearchProduct('');
                    }}
                    size="small"
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #653d91 100%)',
                      }
                    }}
                  >
                    Add Item
                  </Button>
                </Box>
              </Box>

              {items.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 6,
                  border: '2px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  bgcolor: 'grey.50'
                }}>
                  <Inventory sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No items added yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click "Add Item" to start building your invoice
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      if (!category) {
                        alert('Please select a category before adding items to the invoice.');
                        return;
                      }
                      setOpenProductDialog(true);
                      setSearchProduct('');
                    }}
                  >
                    Add First Item
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Items Table */}
                  <TableContainer 
                    component={Paper} 
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>HUID</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Weight (g)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rate</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>GST%</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Final Amount</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item, index) => (
                          <TableRow 
                            key={index}
                            sx={{ 
                              '&:hover': { bgcolor: 'action.hover' },
                              '&:last-child td': { border: 0 }
                            }}
                          >
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {item.product?.name || item.description}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.product?.item_code || '-'} • {item.product?.category || category}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                {item.description}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={item.huid || 'N/A'} 
                                size="small" 
                                variant="outlined"
                                sx={{ maxWidth: 120 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={item.qty} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium">
                                {parseFloat(item.weight || 0).toFixed(3)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium">
                                ₹{parseFloat(item.rate || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">{item.gst_percent || 3}%</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                ₹{Math.round(parseFloat(item.amount || 0)).toLocaleString('en-IN')}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={item.final_amount || ''}
                                onChange={(e) => handleFinalAmountChange(index, e.target.value)}
                                placeholder="Final"
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                  inputProps: { min: 0, step: 0.01 },
                                  sx: { width: 130 }
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Remove Item">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveItem(index)}
                                  color="error"
                                  sx={{ 
                                    '&:hover': { 
                                      bgcolor: 'error.light',
                                      color: 'white'
                                    }
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Old Metal Exchange - Modern Card */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                  <Inventory />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Old Metal Exchange
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add old metal items for exchange credit
                  </Typography>
                </Box>
              </Box>
              
              {/* Add Old Metal Form - Modern */}
              <Box sx={{ 
                mb: 3, 
                p: 3, 
                bgcolor: 'warning.50', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'warning.100'
              }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={newOldMetal.description}
                      onChange={(e) => setNewOldMetal(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="e.g., Gold Chain, Silver Ring"
                      size="small"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Weight (g)"
                      type="number"
                      value={newOldMetal.weight}
                      onChange={(e) => setNewOldMetal(prev => ({ ...prev, weight: e.target.value }))}
                      inputProps={{ step: "0.001", min: 0 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Rate per Gram"
                      type="number"
                      value={newOldMetal.rate}
                      onChange={(e) => setNewOldMetal(prev => ({ ...prev, rate: e.target.value }))}
                      inputProps={{ step: "0.01", min: 0 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Total Amount"
                      type="number"
                      value={newOldMetal.amount || (newOldMetal.weight && newOldMetal.rate ? (parseFloat(newOldMetal.weight) * parseFloat(newOldMetal.rate)).toFixed(2) : '')}
                      InputProps={{
                        readOnly: true,
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                      size="small"
                      sx={{ bgcolor: 'white' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleAddOldMetal}
                      disabled={!newOldMetal.description || !newOldMetal.weight || !newOldMetal.rate}
                      size="small"
                      sx={{
                        bgcolor: 'warning.main',
                        '&:hover': {
                          bgcolor: 'warning.dark',
                        }
                      }}
                    >
                      + Add Old Metal
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Old Metal List */}
              {oldMetals.length > 0 ? (
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    borderRadius: 2,
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Weight (g)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rate</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {oldMetals.map((metal, index) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            '&:hover': { bgcolor: 'action.hover' },
                            '&:last-child td': { border: 0 }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {metal.description}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {parseFloat(metal.weight || 0).toFixed(3)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              ₹{parseFloat(metal.rate || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              ₹{Math.round(parseFloat(metal.amount || 0)).toLocaleString('en-IN')}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Remove Old Metal">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveOldMetal(index)}
                                color="error"
                                sx={{ 
                                  '&:hover': { 
                                    bgcolor: 'error.light',
                                    color: 'white'
                                  }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  border: '2px dashed',
                  borderColor: 'warning.300',
                  borderRadius: 2,
                  bgcolor: 'warning.50'
                }}>
                  <AttachMoney sx={{ fontSize: 48, color: 'warning.400', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No old metal items added yet
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Modes Section - Left Side */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                  <Payment />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Payment Modes
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select payment methods and enter amounts
              </Typography>
              
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {activePaymentModes.map((mode) => {
                  const isSelected = paymentModes.some(p => p.modeId === mode.code);
                  const selectedPayment = paymentModes.find(p => p.modeId === mode.code);
                  
                  return (
                    <Accordion 
                      key={mode.id} 
                      expanded={isSelected}
                      onChange={() => handlePaymentModeToggle(mode)}
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{ 
                          backgroundColor: isSelected ? 'primary.light' : 'grey.50',
                          '&:hover': { backgroundColor: isSelected ? 'primary.light' : 'grey.100' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handlePaymentModeToggle(mode)}
                            onClick={(e) => e.stopPropagation()}
                            color="primary"
                          />
                          <Box sx={{ color: mode.color || 'primary.main', display: 'flex', alignItems: 'center' }}>
                            {getPaymentIcon(mode.icon)}
                          </Box>
                          <Typography variant="body1" fontWeight={isSelected ? 'bold' : 'normal'}>
                            {mode.name}
                          </Typography>
                          {isSelected && selectedPayment?.amount && (
                            <Chip 
                              label={`₹${selectedPayment.amount}`} 
                              size="small" 
                              color="primary" 
                              sx={{ ml: 'auto' }}
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            fullWidth
                            label="Amount (₹)"
                            type="number"
                            value={selectedPayment?.amount || ''}
                            onChange={(e) => handlePaymentAmountChange(mode.code, e.target.value)}
                            placeholder="Enter amount"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            inputProps={{ step: "0.01", min: 0 }}
                          />
                          
                          {mode.requires_reference && (
                            <TextField
                              fullWidth
                              label="Transaction Reference"
                              value={selectedPayment?.reference || ''}
                              onChange={(e) => handlePaymentReferenceChange(mode.code, e.target.value)}
                              placeholder="Enter transaction reference number"
                            />
                          )}
                          
                          {mode.requires_bank && (
                            <TextField
                              fullWidth
                              label="Bank Details"
                              value={selectedPayment?.bankDetails || ''}
                              onChange={(e) => handlePaymentBankDetailsChange(mode.code, e.target.value)}
                              multiline
                              rows={2}
                              placeholder="Enter bank name, account number, etc."
                            />
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>

              {/* Payment Summary */}
              {paymentModes.length > 0 && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'success.50', 
                  borderRadius: 2, 
                  mt: 2,
                  border: '1px solid',
                  borderColor: 'success.100'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Payment Summary
                  </Typography>
                  {paymentModes.map((payment) => (
                    <Box key={payment.modeId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{payment.modeName}:</Typography>
                      <Typography variant="body2" fontWeight="bold">₹{Math.round(parseFloat(payment.amount || 0))}</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Total Paid:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">₹{Math.round(calculatePaymentTotal())}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" fontWeight="bold" color={getRemainingAmount() > 0 ? 'error.main' : 'success.main'}>
                      {getRemainingAmount() > 0 ? 'Balance Amount:' : 'Overpaid:'}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color={getRemainingAmount() > 0 ? 'error.main' : 'success.main'}>
                      ₹{Math.round(Math.abs(getRemainingAmount()))}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice Summary - Right Side */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  <Receipt />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Invoice Summary
                </Typography>
              </Box>
              
              {/* Discount Input */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Discount (%)"
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 100 }}
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">%</InputAdornment>,
                  }}
                />
              </Box>

              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">Sub Total:</Typography>
                  <Typography variant="body2" fontWeight="medium">₹{totals.subtotal}</Typography>
                </Box>
                {discount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="error.main">Less: Discount ({discount}%):</Typography>
                    <Typography variant="body2" fontWeight="medium" color="error.main">-₹{totals.discountAmount}</Typography>
                  </Box>
                )}
                {oldMetals.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="error.main">Less: Old Metal Exchange:</Typography>
                    <Typography variant="body2" fontWeight="medium" color="error.main">-₹{totals.oldMetalTotal}</Typography>
                  </Box>
                )}
                {selectedChits.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="error.main">Less: Chit Amount:</Typography>
                    <Typography variant="body2" fontWeight="medium" color="error.main">-₹{totals.chitTotal}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, mt: 1.5 }}>
                  <Typography variant="body2" fontWeight="medium">Taxable Value:</Typography>
                  <Typography variant="body2" fontWeight="bold">₹{totals.subtotalAfterDeductions}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">GST (3%):</Typography>
                  <Typography variant="body2" fontWeight="medium">₹{totals.gstAmount}</Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight="bold">Grand Total:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">₹{totals.grandTotal}</Typography>
                </Box>
              </Box>
              
              {/* Payment Validation */}
              {paymentModes.length > 0 && (
                <Box sx={{ mb: 2, mt: 2 }}>
                  {Math.abs(getRemainingAmount()) <= 0.01 ? (
                    <Alert 
                      severity="success" 
                      icon={<CheckCircle />}
                      sx={{ 
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          alignItems: 'center'
                        }
                      }}
                    >
                      Payment amount matches invoice total
                    </Alert>
                  ) : (
                    <Alert 
                      severity="warning" 
                      icon={<ErrorIcon />}
                      sx={{ 
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          alignItems: 'center'
                        }
                      }}
                    >
                      Payment total does not match invoice total. 
                      {getRemainingAmount() > 0 ? ' Add more payment.' : ' Reduce payment amount.'}
                    </Alert>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleSubmit}
                  disabled={loading || items.length === 0}
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    py: 1.5,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #653d91 100%)',
                    }
                  }}
                >
                  Update Invoice
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>

      {/* Product Selection Dialog - Modern */}
      <Dialog 
        open={openProductDialog} 
        onClose={() => setOpenProductDialog(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'secondary.main',
          color: 'white',
          pb: 2.5,
          pt: 3,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                <Search sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Select Product
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Choose a product to add to the invoice
                </Typography>
              </Box>
            </Box>
            {category && (
              <Chip 
                label={category}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 'bold',
                  height: 32
                }}
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          {/* Search Bar - Modern */}
          <Box sx={{ mb: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Search Products"
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              placeholder="Search by name, item code, or category..."
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchProduct && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchProduct('')}
                      edge="end"
                    >
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'grey.100' },
                  '&.Mui-focused': { bgcolor: 'white' }
                }
              }}
            />
          </Box>

          {/* Products Table - Modern */}
          {filteredProducts.filter(product => 
            product.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
            product.item_code.toLowerCase().includes(searchProduct.toLowerCase())
          ).length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              bgcolor: 'grey.50'
            }}>
              <Inventory sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No products found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchProduct ? 'Try adjusting your search terms' : 'No products available for this category'}
              </Typography>
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: '50vh',
                '& .MuiTableContainer-root': {
                  overflowX: 'auto'
                }
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Item Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Product Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="center">Category</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="right">Weight (g)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }} align="right">Price/Gram</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="center">Stock</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="center">HUID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts
                    .filter(product => 
                      product.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
                      product.item_code.toLowerCase().includes(searchProduct.toLowerCase())
                    )
                    .map((product) => (
                      <TableRow 
                        key={product.id}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: 'action.hover',
                            cursor: 'pointer'
                          },
                          '&:last-child td': { border: 0 }
                        }}
                        onClick={() => {
                          setSelectedProduct(product);
                          setOpenProductDialog(false);
                          setWeight(product.weight || '');
                          setRate(product.price_per_gram || '');
                          setDescription(product.name || '');
                          setHuid(product.huid || '');
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" fontFamily="monospace">
                            {product.item_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {toTitleCase(product.name)}
                            </Typography>
                            {product.hsn_code && (
                              <Typography variant="caption" color="text.secondary">
                                HSN: {product.hsn_code}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={product.category} 
                            size="small" 
                            color={
                              product.category === 'Gold' ? 'warning' :
                              product.category === 'Silver' ? 'default' : 'info'
                            }
                            sx={{ 
                              fontWeight: 'medium',
                              minWidth: 80
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {product.weight ? parseFloat(product.weight).toFixed(3) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="success.dark">
                            {product.price_per_gram ? `₹${parseFloat(product.price_per_gram).toFixed(2)}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={product.stock_qty || 0} 
                            size="small" 
                            color={!product.stock_qty || product.stock_qty <= 5 ? 'error' : 'success'}
                            sx={{ 
                              fontWeight: 'bold',
                              minWidth: 60
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {product.huid ? (
                            <Chip 
                              label={product.huid} 
                              size="small" 
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem',
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProduct(product);
                              setOpenProductDialog(false);
                              setWeight(product.weight || '');
                              setRate(product.price_per_gram || '');
                              setDescription(product.name || '');
                              setHuid(product.huid || '');
                            }}
                            variant="contained"
                            color="primary"
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              px: 2,
                              boxShadow: 'none',
                              '&:hover': {
                                boxShadow: 2,
                                transform: 'translateY(-1px)'
                              }
                            }}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Products Count */}
          {filteredProducts.filter(product => 
            product.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
            product.item_code.toLowerCase().includes(searchProduct.toLowerCase())
          ).length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Showing {filteredProducts.filter(product => 
                  product.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
                  product.item_code.toLowerCase().includes(searchProduct.toLowerCase())
                ).length} product(s)
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button 
            onClick={() => setOpenProductDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 100
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Details Dialog - Modern */}
      {selectedProduct && (
        <Dialog 
          open={!!selectedProduct} 
          onClose={() => setSelectedProduct(null)} 
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: 'primary.main',
            color: 'white',
            pb: 2.5,
            pt: 3,
            px: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                <Inventory sx={{ fontSize: 28 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Add Item Details
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Enter item information for the invoice
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          
          {/* Product Info Card */}
          <Box sx={{ px: 3, pt: 3, pb: 0 }}>
            <Card sx={{ 
              bgcolor: 'primary.50', 
              border: '1px solid',
              borderColor: 'primary.100',
              borderRadius: 2,
              p: 2.5,
              mb: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <Inventory sx={{ fontSize: 32 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.dark" gutterBottom>
                    {toTitleCase(selectedProduct.name)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    <Chip 
                      label={selectedProduct.item_code} 
                      size="small" 
                      variant="outlined"
                      sx={{ borderColor: 'primary.main', color: 'primary.dark' }}
                    />
                    <Chip 
                      label={selectedProduct.category} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                    {selectedProduct.weight && (
                      <Chip 
                        label={`${parseFloat(selectedProduct.weight).toFixed(3)}g`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {selectedProduct.price_per_gram && (
                      <Chip 
                        label={`₹${parseFloat(selectedProduct.price_per_gram).toFixed(2)}/g`}
                        size="small"
                        variant="outlined"
                        color="success"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
          </Box>

          <DialogContent sx={{ px: 3, pb: 2 }}>
            <Grid container spacing={3}>
              {/* Quantity and Weight Row */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  inputProps={{ min: 1 }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ color: 'text.secondary' }}>#</Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&.Mui-focused': { bgcolor: 'white' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Weight (grams)"
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={selectedProduct.weight ? parseFloat(selectedProduct.weight).toFixed(3) : 'Enter weight'}
                  inputProps={{ step: "0.001", min: 0 }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ color: 'text.secondary' }}>g</Box>
                      </InputAdornment>
                    ),
                  }}
                  helperText={selectedProduct.weight && `Default: ${parseFloat(selectedProduct.weight).toFixed(3)}g`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&.Mui-focused': { bgcolor: 'white' }
                    }
                  }}
                />
              </Grid>

              {/* Rate per Gram */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Rate per Gram"
                  type="number"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder={selectedProduct.price_per_gram ? parseFloat(selectedProduct.price_per_gram).toFixed(2) : 'Enter rate'}
                  inputProps={{ step: "0.01", min: 0 }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={selectedProduct.price_per_gram && `Default: ₹${parseFloat(selectedProduct.price_per_gram).toFixed(2)}/g`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&.Mui-focused': { bgcolor: 'white' }
                    }
                  }}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter custom description (optional)"
                  multiline
                  rows={3}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                        <Receipt sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&.Mui-focused': { bgcolor: 'white' }
                    }
                  }}
                />
              </Grid>

              {/* HUID */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="HUID"
                  value={huid}
                  onChange={(e) => setHuid(e.target.value)}
                  placeholder={selectedProduct.huid || 'Enter HUID (optional)'}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Inventory sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={selectedProduct.huid && `Product HUID: ${selectedProduct.huid}`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&.Mui-focused': { bgcolor: 'white' }
                    }
                  }}
                />
              </Grid>

              {/* Calculated Total Display */}
              {(weight && rate) && (
                <Grid item xs={12}>
                  <Card sx={{ 
                    bgcolor: 'success.50',
                    border: '2px solid',
                    borderColor: 'success.main',
                    borderRadius: 2,
                    p: 2.5,
                    boxShadow: '0 2px 8px rgba(46,125,50,0.1)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                          <AttachMoney sx={{ fontSize: 24 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Item Total (with GST)
                          </Typography>
                          <Typography variant="h5" fontWeight="bold" color="success.dark">
                            ₹{calculateItemTotal({
                              weight: parseFloat(weight) || 0,
                              rate: parseFloat(rate) || 0,
                            }).toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Base Amount
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" color="text.secondary">
                          ₹{((parseFloat(weight) || 0) * (parseFloat(rate) || 0)).toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          + GST (3%)
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
            <Button 
              onClick={() => setSelectedProduct(null)}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                minWidth: 100
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddItem} 
              variant="contained"
              disabled={!quantity || !weight || !rate}
              startIcon={<Add />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                minWidth: 120,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #653d91 100%)',
                },
                '&.Mui-disabled': {
                  background: 'grey.300'
                }
              }}
            >
              Add Item
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Item Confirmation Dialog */}
      <Dialog
        open={deleteItemDialog.open}
        onClose={handleCancelDeleteItem}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'error.main',
          color: 'white',
          pb: 2.5,
          pt: 3,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
              <Delete sx={{ fontSize: 28 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Delete Item
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Are you sure you want to delete this item?
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          {deleteItemDialog.index !== null && items[deleteItemDialog.index] && (
            <Box sx={{ 
              bgcolor: 'grey.50',
              borderRadius: 2,
              p: 2.5,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Item Details:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                  <strong>Product:</strong> {items[deleteItemDialog.index].product?.name || items[deleteItemDialog.index].description}
                </Typography>
                <Typography variant="body2">
                  <strong>Quantity:</strong> {items[deleteItemDialog.index].qty}
                </Typography>
                <Typography variant="body2">
                  <strong>Weight:</strong> {parseFloat(items[deleteItemDialog.index].weight || 0).toFixed(3)}g
                </Typography>
                <Typography variant="body2">
                  <strong>Rate:</strong> ₹{parseFloat(items[deleteItemDialog.index].rate || 0).toFixed(2)}/g
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="error.main" sx={{ mt: 1 }}>
                  Amount: ₹{Math.round(parseFloat(items[deleteItemDialog.index].amount || 0)).toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>
          )}
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={handleCancelDeleteItem}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 100
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteItem}
            variant="contained"
            color="error"
            startIcon={<Delete />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 120
            }}
          >
            Delete Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer Search Dialog - Modern */}
      <Dialog
        open={openCustomerSearchDialog}
        onClose={() => {
          setOpenCustomerSearchDialog(false);
          setCustomerSearchTerm('');
          setCustomerSearchResults([]);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main',
          color: 'white',
          pb: 2.5,
          pt: 3,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                <PhoneAndroid sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Select Customer
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Search and choose a customer for the invoice
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => {
                setOpenCustomerSearchDialog(false);
                setCustomerSearchTerm('');
                setCustomerSearchResults([]);
              }}
              size="small"
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          {/* Search Bar - Modern */}
          <Box sx={{ mb: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Search Customers"
              value={customerSearchTerm}
              onChange={(e) => setCustomerSearchTerm(e.target.value)}
              placeholder="Search by name, phone, or GSTIN..."
              size="small"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: customerSearchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setCustomerSearchTerm('')}
                      edge="end"
                    >
                      <Cancel sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'grey.100' },
                  '&.Mui-focused': { bgcolor: 'white' }
                }
              }}
            />
          </Box>

          {/* Customers List */}
          {customerSearchLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : customerSearchResults.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              bgcolor: 'grey.50'
            }}>
              <PhoneAndroid sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No customers found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customerSearchTerm ? 'Try adjusting your search terms' : 'Start typing to search customers...'}
              </Typography>
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: '50vh',
                '& .MuiTableContainer-root': {
                  overflowX: 'auto'
                }
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Customer Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>GSTIN</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerSearchResults.map((cust) => (
                    <TableRow 
                      key={cust.id}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: 'action.hover',
                          cursor: 'pointer'
                        },
                        '&:last-child td': { border: 0 },
                        bgcolor: customer?.id === cust.id ? 'primary.50' : 'transparent'
                      }}
                      onClick={() => {
                        setCustomer(cust);
                        setOpenCustomerSearchDialog(false);
                        setCustomerSearchTerm('');
                        setCustomerSearchResults([]);
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {toTitleCase(cust.name || '')}
                          </Typography>
                          {customer?.id === cust.id && (
                            <CheckCircle color="primary" sx={{ fontSize: 18 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {cust.phone || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {cust.gstin ? (
                          <Chip
                            label={cust.gstin}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 250 }}>
                          {cust.address || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCustomer(cust);
                            setOpenCustomerSearchDialog(false);
                            setCustomerSearchTerm('');
                            setCustomerSearchResults([]);
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={() => {
              setOpenCustomerSearchDialog(false);
              setCustomerSearchTerm('');
              setCustomerSearchResults([]);
            }}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialog.open}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          bgcolor: 'success.main',
          color: 'white',
          pb: 2,
          pt: 2.5
        }}>
          <Box 
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              borderRadius: '50%', 
              p: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: `${tickAnimation} 0.6s ease-in-out`
            }}
          >
            <CheckCircle 
              sx={{ 
                fontSize: 28,
                animation: `${scaleIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)`
              }} 
            />
          </Box>
          <Typography variant="h6" fontWeight="bold">
            {successDialog.title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography 
            variant="body1" 
            color="text.primary" 
            sx={{ 
              whiteSpace: 'pre-line',
              mb: 3,
              textAlign: 'center',
              fontWeight: 500
            }}
          >
            {successDialog.message}
          </Typography>
          
          {/* Invoice Details */}
          {successDialog.invoiceNo && (
            <Box sx={{ 
              bgcolor: 'success.50', 
              borderRadius: 2, 
              p: 2.5,
              border: '1px solid',
              borderColor: 'success.200',
              mb: 2
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Invoice Number */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    Invoice Number:
                  </Typography>
                  <Typography variant="body1" color="text.primary" fontWeight="bold">
                    {successDialog.invoiceNo}
                  </Typography>
                </Box>
                
                {/* Invoice Date */}
                {successDialog.invoiceDate && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Invoice Date:
                    </Typography>
                    <Typography variant="body1" color="text.primary" fontWeight="bold">
                      {new Date(successDialog.invoiceDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Box>
                )}
                
                {/* Amount */}
                {successDialog.amount && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1,
                    bgcolor: 'success.100',
                    borderRadius: 1,
                    px: 1.5,
                    mt: 0.5
                  }}>
                    <Typography variant="body2" color="success.dark" fontWeight="bold">
                      Total Amount:
                    </Typography>
                    <Typography 
                      variant="h6" 
                      color="success.dark" 
                      fontWeight="bold"
                      sx={{
                        animation: `${fadeInUp} 0.5s ease-out 0.2s both`
                      }}
                    >
                      ₹{parseFloat(successDialog.amount).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="contained"
            color="success"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1,
              minWidth: 100,
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
              }
            }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditInvoice;